import { routes } from "@/routes/utils";
import { useForm, useWatch } from "react-hook-form";
import { ADDRESS, ADDRESS_1, ALTERNATE_LOGIN_ID, CITY, COMMUNITY, CORRESPONDENCE_ADDRESS, CUSTOM_ADDRESS, EMAIL_ID_1, EMAIL_ID_2, END_DATE, EXISTING, FIRST_NAME, INDIVIDUAL, LANDLINE_NO, LAST_NAME, MOBILE_NO, MULTI_CORRESPOND, NEW, NEW_USER, OCCUPANCY_TYPE, ORGANIZATION_NAME, orgOwnershipType, OWNER, OWNERSHIP_SHARE, OWNERSHIP_TYPE, PO_BOX, PRIMARY_USER, PROPERTY, START_DATE, STATE, USER_ID, USER_TYPE, USERNAME, USERS, ZIPCODE } from "./consts";
import { zodResolver } from "@hookform/resolvers/zod";
import { formValues, schema } from "./validations";
import { useAddPropertyOwnerMutation, useGetCommunities, useGetConsentDtl, useGetConsentNeighbor, useGetPropertiesByCommunity, useGetPropertyOwnerByIdV2, useGetState, useUpdatePropertyOwnerV2Mutation } from "@/services";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { formatValidateUserDataErrors, getBaseRoute, getDuplicateNewUserFieldChecks, getRequiredFields } from "@/lib/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { AddressType } from "./types";
import { toast } from "@/components/Toast";
import moment from "moment";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

const useAddEdit = () => {
  const { userId, action, id, propertyId } = useParams();
  const isView = action == "view";
  const isEdit = action == "edit";
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const isFromMasterData = location.pathname?.includes("/master-data");
  const queryClient = useQueryClient();
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const propertyData = useSelector((state: RootState) => state.currentData.propertyData);
  const basePath = getBaseRoute(useLocation().pathname);
  const isAdminBasePath = basePath == "admin";

  const form = useForm<formValues>({
    defaultValues: {
      [COMMUNITY]: communityData?.communityId?.toString(),
      [PROPERTY]: userId || !isFromMasterData ? propertyData?.propertyId?.toString() : "",
      [OWNERSHIP_TYPE]: INDIVIDUAL,
      [MULTI_CORRESPOND]: false,
      [CORRESPONDENCE_ADDRESS]: "property",
      [ORGANIZATION_NAME]: "",
      [USERS]: [],
      [CUSTOM_ADDRESS]: null
    },
    resolver: zodResolver(schema),
  });

  const [openAddEditDialog, setOpenAddEditDialog] = useState<boolean>(false);
  const [openRemoveOwnerDialog, setOpenRemoveOwnerDialog] = useState<boolean>(false);
  const [openDeleteSingleOwnerDialog, setOpenDeleteSingleOwnerDialog] = useState<boolean>(false);
  const [keys, setKeys] = useState({
    state: Math.random() * 2
  });

  const dialogAction = useRef("add");
  const selectedUser = useRef<any>(null);

  const requiredFields = getRequiredFields(schema);
  const formData = useWatch({ control: form.control });

  const communityId = useWatch({ control: form.control, name: COMMUNITY});

  const onBackClick = () => {
    if (isFromMasterData) {
      navigate(`/${basePath}/${routes.masterData}/${routes.owner}`)
    } else {
      navigate(isAdminBasePath ? `/${basePath}/${routes.adminConsole}/${routes.communities}/${id}/${routes.properties}/${propertyId}/${routes.users}` : `/${basePath}/${routes.communities}/${id}/${routes.properties}/${propertyId}/${routes.users}`);
    }
  }

  const { data: communityList, isLoading: isCommunityLoading } =
    useGetCommunities({});

  const { data: propertyList, isLoading: isPropertyLoading } =
    useGetPropertiesByCommunity({
      communityId: communityId as string,
      queryParams: {
        enabled: !!communityId
      }
    });

  const { data: ownershipDtl, isLoading: isOwnershipDtlLoading } =
    useGetPropertyOwnerByIdV2({
      communityId: communityData?.communityId,
      id: userId as string,
      queryParams: {
        enabled: !!userId
      }
    });

  const { data: consentDtl, isLoading: isLoadingConsentDtl } = useGetConsentDtl({
    communityId: communityData?.communityId,
    propertyId: propertyData?.propertyId,
    queryParams: {
      enabled: !!(communityData?.communityId || propertyData?.propertyId)
    }
  });

  const { data: neighborList, isLoading: isLoadingNeighborList} = useGetConsentNeighbor({
    communityId: communityData?.communityId,
    propertyId: propertyData?.propertyId,
    type: "i-share-with",
    queryParams: {
      enabled: !!(communityData?.communityId && propertyData?.propertyId)
    }
  });

const title = useMemo(() => {
    return isView ? `View ${ownershipDtl?.data?.firstName || ""} ${ownershipDtl?.data?.lastName || ""} Details`
    : isEdit
      ? `Edit ${ownershipDtl?.data?.firstName || ""} ${ownershipDtl?.data?.lastName || ""} Details`
      : "Add User";
  }, [ownershipDtl]);

  const breadcrumbDataMasterData = [
    {
      link: `/${basePath}/${routes.masterData}/${routes.owner}`,
      name: `Users - ${communityData?.name}`
    },
    {
      link: "",
      name: isView ? `View ${ownershipDtl?.data?.firstName || ""} ${ownershipDtl?.data?.lastName || ""} Details`
            : isEdit
              ? `Edit ${ownershipDtl?.data?.firstName || ""} ${ownershipDtl?.data?.lastName || ""} Details`
              : "Add User"
    },
  ];

  const breadcrumbDataProperty = useMemo(
    () => [
      {
        link: `/${basePath}/${routes.adminConsole}/${routes.communities}`,
        name: `Community - ${communityData?.name || ""}`,
      },
      {
        link: `/${basePath}/${routes.adminConsole}/${routes.communities}/${id}/${routes.properties}`,
        name: `Property - ${propertyData?.name || ""}`,
      },
      {
        link: `/${basePath}/${routes.adminConsole}/${routes.communities}/${id}/${routes.properties}/${propertyId}/${routes.users}`,
        name: "Manage Users",
      },
      {
        link: "",
        name: (
          <span className="text-primary">
            {isView
              ? `View ${ownershipDtl?.data?.firstName || ""} ${
                ownershipDtl?.data?.lastName || ""
              } Details`
              : isEdit
              ? `Edit ${ownershipDtl?.data?.firstName || ""} ${
                ownershipDtl?.data?.lastName || ""
              } Details`
              : "Add User"}
          </span>
        ),
      },
    ],
    [ownershipDtl]
  );

  const breadcrumbData = isFromMasterData ? breadcrumbDataMasterData : breadcrumbDataProperty;
  const { data: stateList, isLoading: isStateLoading } = useGetState({});
  
  useEffect(() => {
    if (ownershipDtl) {
      const data = ownershipDtl?.data;
      form.reset({
        [COMMUNITY]: data?.communityId?.toString(),
        [PROPERTY]: data?.[PROPERTY]?.toString() || "",
        [OWNERSHIP_TYPE]: data?.[OWNERSHIP_TYPE] || INDIVIDUAL,
        [MULTI_CORRESPOND]: data?.[MULTI_CORRESPOND] || false,
        [CORRESPONDENCE_ADDRESS]: data?.[CORRESPONDENCE_ADDRESS] || "property",
        [CUSTOM_ADDRESS]: data?.[CUSTOM_ADDRESS] || null,
        [USERS]: data?.[USERS]?.map((usr: any) => ({
          ...usr,
          [USER_ID]: usr?.[USER_ID]?.toString(),
          [ALTERNATE_LOGIN_ID]: usr?.[ALTERNATE_LOGIN_ID]?.toString(),
          [OWNERSHIP_SHARE]: usr?.[OWNERSHIP_SHARE]?.toString(),
          [USER_TYPE]: EXISTING,
          [START_DATE]: usr?.[START_DATE] ? usr?.[START_DATE]?.toString() : "",
          [END_DATE]: usr?.[END_DATE] ? usr?.[END_DATE]?.toString() : ""
        }))
      })
      setKeys({
        state: Math.random() * 3
      })
    }
  }, [ownershipDtl]);

  const { mutate: addOwnership, isPending: isAddOwnershipPending } =
    useAddPropertyOwnerMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Ownership Added Successfully");
        onBackClick();
        queryClient.invalidateQueries({queryKey: ["communityOccupancyList", communityId]})
      },
    });

  const { mutate: updateOwnership, isPending: isUpdateOwnershipPending } =
    useUpdatePropertyOwnerV2Mutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Ownership Updated Successfully");
        onBackClick();
        queryClient.invalidateQueries({queryKey: ["communityOccupancyList", communityId]})
      },
    });
    
  const onOwnershipTypeChange = (type: any) => () => {
    form.setValue(OWNERSHIP_TYPE, type);
  };

  const onAddUser = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    dialogAction.current = "add";
    selectedUser.current = null;
    setOpenAddEditDialog(true);
  }

  const onSave = (data: any) => {
    let users = [...(form.getValues(USERS) || [])];

    if (data[USER_TYPE] == NEW || !data[USER_ID]) {
      const msg = formatValidateUserDataErrors(
        getDuplicateNewUserFieldChecks(data, users, data.rowIndex)
      );
      if (msg) {
        toast.error(msg);
        return;
      }
    }

    if (data.rowIndex) {
      users[data.rowIndex] = {...data};
    } else {
      users = [...users, data];
    }

    const isPrimaryUserExist = users.some((data: any) => data[PRIMARY_USER]);

    if (!isPrimaryUserExist) {
      users[0] = {...users[0], [PRIMARY_USER]: true}
    }

    form.setValue(USERS, [...users]);
    setOpenAddEditDialog(false);
  }

  const onRemoveSave = (data: any) => {
    let users = [...(formData as any)[USERS]];
    const deletedIdx = users.findIndex((usr: any) => usr.userId == selectedUser.current.userId);
    users[deletedIdx] = {...users[deletedIdx], delete: true};
    form.setValue(USERS, [...users, ...data]);
    setOpenRemoveOwnerDialog(false);
  }

  const onEditUser = (user: any, idx: number) => {
    dialogAction.current = "edit";
    selectedUser.current = {...user, rowIndex: idx?.toString()};
    setOpenAddEditDialog(true);
  }

  const onAddressTypeChange = (value: AddressType) => {
    form.setValue(CORRESPONDENCE_ADDRESS, value)
  }
  
  const addUserDisabled = useMemo(() => {
    return formData[OWNERSHIP_TYPE] == INDIVIDUAL && formData[USERS]?.length == 1 ? true : false
  }, [formData]);

  const validateCustomAddress = (address: any) => {
    const requiredFields = [
      { key: ADDRESS_1, name: ADDRESS_1 },
      { key: STATE, name: STATE },
      { key: CITY, name: CITY },
      { key: ZIPCODE, name: ZIPCODE },
      { key: PO_BOX, name: PO_BOX },
    ];
  
    let isValid = true;
  
    if (formData[CORRESPONDENCE_ADDRESS] == "custom") {
      requiredFields.forEach(({ key, name }) => {
        if (!address?.[key]?.trim?.() && !address?.[key]) {
          form.setError(`${CUSTOM_ADDRESS}.${name}` as any, {
            type: "manual",
            message: "This Field is required",
          });
          isValid = false;
        }
      });
    }
  
    return isValid;
  }

  const onSubmit = (data: any) => {
    if (orgOwnershipType.includes((formData as any)[OWNERSHIP_TYPE]) && !data[ORGANIZATION_NAME]) {
      form.setError(ORGANIZATION_NAME, {message: "This Field is required"});
      return;
    } 
    
    const isOwnerExist = data[USERS]?.find((usr: any) => usr?.[OCCUPANCY_TYPE] == OWNER);

    if (!isOwnerExist) {
      toast.error("Please add at least one owner to continue.");
      return;
    }

    if (validateCustomAddress(data[CUSTOM_ADDRESS])) {
      if (isEdit) {
        updateOwnership({
          communityId: communityData?.communityId,
          id: userId as string,
          data: {...data, [USERS]: data?.[USERS]?.map((usr: any) => {
            const obj = {
              [OCCUPANCY_TYPE]: usr[OCCUPANCY_TYPE],
              [PRIMARY_USER]: usr[PRIMARY_USER],
              [START_DATE]: usr[START_DATE] ? moment(usr[START_DATE]).format('YYYY-MM-DDT00:00:00') : null,
              [END_DATE]: usr[END_DATE] ? moment(usr[END_DATE]).format('YYYY-MM-DDT23:59:59') : null,
              [OWNERSHIP_SHARE]: usr[OCCUPANCY_TYPE] == OWNER ? usr[OWNERSHIP_SHARE] : ""
            }
    
            if (usr.userId) {
              return {
                [USER_ID]: usr[USER_ID],
                ...obj
              }
            } else {
              return {
                [NEW_USER]: {
                  [USERNAME]: usr[USERNAME],
                  [ALTERNATE_LOGIN_ID]: usr[ALTERNATE_LOGIN_ID],
                  "title": "",
                  [FIRST_NAME]: usr[FIRST_NAME],
                  [LAST_NAME]: usr[LAST_NAME],
                  [MOBILE_NO]: usr[MOBILE_NO],
                  [LANDLINE_NO]: usr[LANDLINE_NO],
                  [EMAIL_ID_1]: usr[EMAIL_ID_1],
                  [EMAIL_ID_2]: usr[EMAIL_ID_2],
                  [ADDRESS]: usr[ADDRESS],
                  [PRIMARY_USER]: usr[PRIMARY_USER],
                },
                ...obj
              }
            }
          })}
        })
      } else {
        addOwnership({
          communityId: communityData?.communityId,
          data: {...data, [USERS]: data?.[USERS]?.map((usr: any) => {
            const obj = {
              [OCCUPANCY_TYPE]: usr[OCCUPANCY_TYPE],
              [PRIMARY_USER]: usr[PRIMARY_USER],
              [START_DATE]: usr[START_DATE] ? moment(usr[START_DATE]).format('YYYY-MM-DDT00:00:00') : null,
              [END_DATE]: usr[END_DATE] ? moment(usr[END_DATE]).format('YYYY-MM-DDT23:59:59') : null,
              [OWNERSHIP_SHARE]: usr[OCCUPANCY_TYPE] == OWNER ? usr[OWNERSHIP_SHARE] : ""
            }
    
            if (usr.userId) {
              return {
                [USER_ID]: usr[USER_ID],
                ...obj
              }
            } else {
              return {
                [NEW_USER]: {
                  [USERNAME]: usr[USERNAME],
                  [ALTERNATE_LOGIN_ID]: usr[ALTERNATE_LOGIN_ID],
                  "title": "",
                  [FIRST_NAME]: usr[FIRST_NAME],
                  [LAST_NAME]: usr[LAST_NAME],
                  [MOBILE_NO]: usr[MOBILE_NO],
                  [LANDLINE_NO]: usr[LANDLINE_NO],
                  [EMAIL_ID_1]: usr[EMAIL_ID_1],
                  [EMAIL_ID_2]: usr[EMAIL_ID_2],
                  [ADDRESS]: usr[ADDRESS],
                  [PRIMARY_USER]: usr[PRIMARY_USER],
                },
                ...obj
              }
            }
          })}
        })
      }
    }
  }

  const onChangePrimary = (idx: number) => {
    let usr = [...(formData as any)[USERS]];
    usr = usr.map((data: any) => ({...data, [PRIMARY_USER]: false}))
    usr[idx] = {...usr[idx], [PRIMARY_USER]: true}
    form.setValue(USERS, usr);
  }

  const ownerCount = useMemo(() => {
    return formData[USERS]?.filter((data: any) => data[OCCUPANCY_TYPE]?.toLowerCase() == "owner")?.length || 0
  }, [formData[USERS]]);

  const propertyName = useMemo(() => {
    return propertyList?.data?.find((data: any) => data?.propertyId?.toString() == formData[PROPERTY])?.name || ""
  }, [formData[PROPERTY]]);

  const ownershipShare = useMemo(() => {
    return formData[USERS]?.reduce(
      (acc: number, val: any) => {
        const num = parseFloat(val[OWNERSHIP_SHARE]);
        return acc + (isNaN(num) ? 0 : num);
      },
      0
    );
  }, [formData[USERS]]);

  const primaryContact = useMemo(() => {
    const row = formData[USERS]?.find((data: any) => data?.[PRIMARY_USER]);
    return row ? `${(row as any)[FIRST_NAME]} ${(row as any)[LAST_NAME]}` : ""
  }, [formData[USERS]]);

  const primaryUserDtl = useMemo(() => {
    const row = formData[USERS]?.find((data: any) => data?.[PRIMARY_USER]);
    return row || {};
  }, [formData[USERS]]);

  const primaryUserAddress = useMemo(() => {
    if (formData[CORRESPONDENCE_ADDRESS] == "property") {
      return ""
    } 
    if (formData[CORRESPONDENCE_ADDRESS] == "owner") {
      const row = formData[USERS]?.find((data: any) => data?.[PRIMARY_USER]);
      return (row as any)?.[ADDRESS] ? `${Object.values((row as any)[ADDRESS])?.join(", ")}` : "";
    }
    if (formData[CORRESPONDENCE_ADDRESS] == "custom") {
      return formData?.[CUSTOM_ADDRESS]?.[ADDRESS_1] ?`${Object.values(formData[CUSTOM_ADDRESS])?.join(", ")}` : "";
    }
  }, [formData[CORRESPONDENCE_ADDRESS], formData[CUSTOM_ADDRESS], formData[USERS]]);

  const onRemoveOwner = (usr: any) => {
    selectedUser.current = usr;
    setTimeout(() => {
      setOpenRemoveOwnerDialog(true);
    }, 200);
  }

  const onDeleteIndividualUser = (usr: any) => {
    selectedUser.current = usr;
    setOpenDeleteSingleOwnerDialog(true);
  }

  const onDeleteConfirmIndividualUser = () => {
    let users = [...(formData as any)[USERS]];
    form.setValue(USERS, [...users.filter((usr: any) => usr.userId != selectedUser.current.userId)]);
    setOpenDeleteSingleOwnerDialog(false);
  }

  return {
    breadcrumbData,
    form,
    isCommunityLoading,
    communityList: communityList?.data || [],
    propertyList: propertyList?.data || [],
    isPropertyLoading,
    onOwnershipTypeChange,
    formData,
    onAddUser,
    requiredFields,
    onSave,
    openAddEditDialog,
    setOpenAddEditDialog,
    isMobile,
    onEditUser,
    selectedUser: selectedUser.current,
    action: dialogAction.current,
    onAddressTypeChange,
    isAddOwnershipPending,
    onSubmit,
    addUserDisabled,
    onChangePrimary,
    ownerCount,
    communityData,
    isView,
    title,
    isOwnershipDtlLoading,
    propertyName,
    ownershipShare,
    primaryContact,
    isUpdateOwnershipPending,
    isEdit,
    primaryUserAddress,
    primaryUserDtl,
    openRemoveOwnerDialog,
    setOpenRemoveOwnerDialog,
    onRemoveOwner,
    onRemoveSave,
    isFromMasterData,
    isStateLoading,
    stateList: stateList?.data || [],
    keys,
    onDeleteConfirmIndividualUser,
    onDeleteIndividualUser,
    openDeleteSingleOwnerDialog,
    setOpenDeleteSingleOwnerDialog,
    isLoadingConsentDtl: isLoadingConsentDtl || isLoadingNeighborList,
    consentDtl: consentDtl?.data || [],
    neighborList: neighborList?.data || [],
    isEnabledConsent: consentDtl?.data?.consentEnabled
  }
}

export default useAddEdit;